import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useState, useEffect, useRef, useMemo, useCallback } from "react"

import { rootStore, useStores } from "stores/root-store"

import AlertDialog from "components/common/alert-dialog"
import { LoggingHelper } from "utils/logging-helper"
import BomInfo from "./bom-info"
import BomEditComponents from "./bom-edit-components"
import { FormWrapper } from "components/common-styles"
import { errorType, trackEvent } from "utils/analytics"
import CommonFormActions from "components/common/common-form-actions"
import { ArrowBack } from "@mui/icons-material"
import InventoryForm from "components/inventory/dialog/inventory-form"

const BomForm = ({ onClose, onSave }) => {
  const { BillOfMaterialStore } = useStores()
  const initialValues = useMemo(() => {
    return {
      item: "",
      quantity_um: "EA",
      item_desc: "",
      details: [],
    }
  }, [])

  const [openAlert, setOpenAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")

  const formRef = useRef()
  const componentsRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [bomInfo, setBomInfo] = useState(initialValues)
  const [wasSuccess, setWasSuccess] = useState(true)
  const [openAddSKU, setOpenAddSKU] = useState(false)
  const [newSKU, setNewSKU] = useState("")

  const initData = useCallback(() => {
    if (BillOfMaterialStore.bomInfo) {
      const data = { ...BillOfMaterialStore.bomInfo }
      data.products = JSON.parse(JSON.stringify(data.details || []))
      setBomInfo(data)
    } else {
      setBomInfo(initialValues)
    }
  }, [BillOfMaterialStore.bomInfo, initialValues])

  useEffect(() => {
    initData()
  }, [initData])

  const handleSubmit = async () => {
    const resInfo = await formRef.current.getFormDataIfValid()
    if (!resInfo) return

    const resProducts = await componentsRef.current.getProductsIfValid()
    if (!resProducts) return

    const newInfo = { ...bomInfo, ...resInfo, products: resProducts }

    setIsSubmitting(true)
    setOpenAlert(true)
    const result = await BillOfMaterialStore.addOrUpdateBOM(newInfo, rootStore.userName, rootStore.company)
    LoggingHelper.writeLog("BomForm", "submit", `${result ? JSON.stringify(result) : ""}`)
    setIsSubmitting(false)

    setWasSuccess(true)
    let msg = BillOfMaterialStore.bomInfo ? "Data Updated Successfully" : "New Data Added Successfully"
    if (result && (result.resource?.message || result.message || result["message "] || typeof result == "string")) {
      setWasSuccess(false)
      msg = result.resource?.message || result["message "] || result.message || result
      trackEvent("record_save", { entity: "bom", mode: BillOfMaterialStore.bomInfo ? "edit" : "add", result: "failed", error_type: errorType(result) })
    } else {
      trackEvent("record_save", { entity: "bom", mode: BillOfMaterialStore.bomInfo ? "edit" : "add", result: "success" })
    }
    setAlertContent(msg)
  }

  const onOK = () => {
    setOpenAlert(false)
    if (wasSuccess) {
      onSave()
    }
  }

  const handleClose = () => {
    onClose()
  }

  const handleCloseNewSKU = () => {
    setOpenAddSKU(false)
  }

  const handleSave = newProduct => {
    handleCloseNewSKU()
    setNewSKU(newProduct)
  }

  return openAddSKU ? (
    <InventoryForm onSave={handleSave} onClose={handleCloseNewSKU} creatingBOM={true} />
  ) : (
    <>
      <FormWrapper spacing={2}>
        <Stack direction={"row"} spacing={2}>
          <Tooltip placement="top" title="Back">
            <IconButton onClick={handleClose}>
              <ArrowBack />
            </IconButton>
          </Tooltip>

          <Box>
            {BillOfMaterialStore.bomInfo ? (
              <>
                <Typography variant="h5">Edit Bill of Materials: {BillOfMaterialStore.bomInfo.item}</Typography>
                <small>Review BOM attributes and components on an existing bill of materials</small>
              </>
            ) : (
              <>
                <Typography variant="h5">New Bill of Materials</Typography>
                <small>Enter BOM attributes and components to create a new bill of materials</small>
              </>
            )}
          </Box>
        </Stack>

        <BomInfo bomInfo={bomInfo} ref={formRef} onAddNewSKU={() => setOpenAddSKU(true)} newSKU={newSKU} />
        <BomEditComponents bomInfo={bomInfo} ref={componentsRef} isSubmitting={isSubmitting} />

        <CommonFormActions isSubmitting={isSubmitting} handleSubmit={handleSubmit} handleClose={handleClose} />
      </FormWrapper>
      <AlertDialog isProgress={isSubmitting} content={alertContent} open={openAlert} onClose={() => setOpenAlert(false)} onOK={onOK} />
    </>
  )
}

export default BomForm
